import DocumentAsset from "@/assets/illustrations/document.webp";
import { useQuery } from "@apollo/client/react";
import useChatDocuments, {
  useIsGuestChat,
} from "@repo/components/src/hooks/agent/useChatDocuments";
import { Box } from "@repo/components/src/ui/box";
import { Button, ButtonText } from "@repo/components/src/ui/button";
import { HStack } from "@repo/components/src/ui/hstack";
import { Icon } from "@repo/components/src/ui/icon";
import { Image } from "@repo/components/src/ui/image";
import { Text } from "@repo/components/src/ui/text";
import { VStack } from "@repo/components/src/ui/vstack";
import { formatSimpleMoney } from "@repo/components/src/utils/money";
import { AgentKey, BudgetType } from "@repo/graphql/gql/graphql";
import { Link } from "expo-router";
import { Check } from "lucide-react-native";
import type { ReactNode } from "react";
import { useMemo } from "react";
import { CAMPAIGN_TYPES } from "./campaignTypes";
import { EMPTY_DRAFT_PREVIEW } from "./draftPreviewTypes";
import type { DraftPreviewModel } from "./draftPreviewTypes";
import PublishDraftButton from "./PublishDraftButton";
import { parseDraftPreview } from "./parseDraftPreview";

type Props = {
  brandId: string;
  /** The chat this card lives in; a plan checkout lands back on it. */
  sessionId: string | undefined;
  draftId: string | undefined;
  agentKey: string | undefined;
  /** The preview is a tab of its own, not a panel on the right. */
  stacked?: boolean;
  /** A plan checkout that landed back on this chat is being confirmed. */
  verifyingPayment?: boolean;
};

const FORMAT_WORD: Record<string, string> = {
  VIDEO: "video",
  IMAGE: "imagen",
};

/**
 * "3 videos UGC · $45.000/video" — never a creator count (the draft has no
 * creators yet, that only happens at publish time), only what the draft itself
 * actually says: how many pieces of content, and — if the budget is a `Fixed`
 * amount — what that comes out to per piece.
 */
const statsLine = (
  model: DraftPreviewModel,
  campaignTypeLabel: string | undefined,
): string | null => {
  const count = model.contents.length;
  if (!count) return null;

  const formats = new Set(
    model.contents.map((content) => content.postContentFormatType),
  );
  const word =
    formats.size === 1
      ? (FORMAT_WORD[[...formats][0] ?? ""] ?? "contenido")
      : "contenido";
  const plural = count === 1 ? word : `${word}s`;

  const parts = [
    `${count} ${plural}${campaignTypeLabel ? ` ${campaignTypeLabel}` : ""}`,
  ];

  if (
    model.budgetType === BudgetType.Fixed &&
    model.fixedBudget &&
    Number(model.fixedBudget.amount) > 0
  ) {
    const perItem = Number(model.fixedBudget.amount) / count;
    const formatted = formatSimpleMoney({
      amount: String(perItem),
      currency: model.fixedBudget.currency,
    });
    parts.push(`${formatted}/${word}`);
  }

  return parts.join(" · ");
};

/**
 * Moment 4 — "Draft creado". The final animation-system moment, and the only one
 * that is not "in progress" news: it is the outcome, so `AnimationSlot` treats it
 * as its own highest-priority render (see that component's docstring) rather than
 * something gated on the turn being live.
 *
 * A HOOK, not a component: `AnimationSlot`'s `finalMoment` prop decides whether
 * to show moment 4 purely from whether it received a `ReactNode` — and a mounted
 * `<Component />` element is truthy even when that component internally renders
 * `null`. Returning `ReactNode | null` here, computed *before* the screen ever
 * builds the prop, is what lets the screen tell `AnimationSlot` the honest
 * answer instead of always handing it a (truthy) element.
 *
 * Fetches the draft itself rather than being fed it — this lives in the chat
 * column, not the preview panel, so it has no draft data of its own to read.
 * Apollo dedupes the identical query the preview panel is already running.
 *
 * Owns the "Publicar campaña" action itself now — the founder's call: the one
 * thing this card exists to lead to should not require a trip to a different
 * panel to find the button for it. `creatorPreview/index.tsx` keeps only the
 * "Editar" link. So once the draft is ready, this stays up — button and all —
 * for as long as the draft stays ready, however many more turns it takes to
 * actually publish it. A signed-in brand gets `PublishDraftButton` (plan
 * checks included); a guest (`/start`) has no account to publish into yet, so
 * its button leads to sign-up.
 */
export const useDraftReadyMoment = ({
  brandId,
  sessionId,
  draftId,
  agentKey,
  stacked = false,
  verifyingPayment = false,
}: Props): ReactNode | null => {
  const guest = useIsGuestChat();
  const documents = useChatDocuments();
  const { data } = useQuery(documents.campaignDraft, {
    variables: { id: draftId ?? "" },
    skip: !draftId,
    fetchPolicy: "cache-and-network",
  });
  const draft = data?.campaignDraft.get;
  const ready = !!draft?.ready;

  const model = useMemo(
    () =>
      draft?.draftData
        ? parseDraftPreview(draft.draftData)
        : EMPTY_DRAFT_PREVIEW,
    [draft],
  );

  if (!draftId || !ready) return null;

  const campaignTypeLabel = CAMPAIGN_TYPES.find(
    (type) => type.agentKey === (agentKey as AgentKey),
  )?.label;
  const stats = statsLine(model, campaignTypeLabel);

  return (
    <Box
      key={`draft-ready-${draftId}`}
      className="relative mb-3 w-full self-stretch overflow-hidden rounded-2xl border border-outline-100 bg-background-0 px-5 py-5 animate-fade-rise motion-reduce:animate-none"
    >
      {/* The same brand-tinted glow accent as the chat's own message-list header
          (`chat/messages/list`) — a soft blur, not a solid tint, so the card stays
          "white/soft" per the dashboard Montu AI card rather than going back to a
          block of colour. */}
      <Box className="absolute -right-10 -top-10 h-40 w-40 rounded-full bg-brand-500/10 blur-2xl" />
      <HStack space="md" className="items-center">
        <Box className="relative h-16 w-16 shrink-0 items-center justify-center">
          <Image
            source={DocumentAsset}
            alt=""
            contentFit="contain"
            className="h-16 w-16 animate-spring motion-reduce:animate-none"
          />
          <Box className="absolute -bottom-1 -right-1 items-center justify-center">
            <Box className="absolute h-8 w-8 -translate-x-1 -translate-y-1 rounded-full animate-glowp motion-reduce:animate-none" />
            <Box className="h-6 w-6 items-center justify-center rounded-full border-2 border-background-0 bg-success-500 animate-popin motion-reduce:animate-none">
              <Icon as={Check} size="2xs" className="text-typography-white" />
            </Box>
          </Box>
        </Box>
        <VStack className="flex-1">
          <Text className="text-base font-bold text-typography-900">
            Draft creado
          </Text>
          <Text
            className="text-sm font-medium text-typography-700"
            numberOfLines={2}
          >
            {model.name || "Tu campaña"}
          </Text>
          {stats ? (
            <Text className="text-xs text-typography-500">{stats}</Text>
          ) : null}
        </VStack>
      </HStack>
      <Text className="mt-4 text-sm text-typography-600">
        {stacked
          ? "Revisa la previsualización en su pestaña →"
          : "Revisa la previsualización a la derecha →"}
      </Text>
      {guest ? (
        <Link href={{ pathname: "/start/register" }} asChild>
          <Button
            action="brand"
            variant="solid"
            size="md"
            className="mt-3 w-full"
          >
            <ButtonText>Publicar campaña</ButtonText>
          </Button>
        </Link>
      ) : (
        <PublishDraftButton
          brandId={brandId}
          sessionId={sessionId}
          draftId={draftId}
          verifyingPayment={verifyingPayment}
        />
      )}
    </Box>
  );
};
